/**
 * Shared status configuration — single source of truth for trip and client statuses.
 *
 * Consumed by: Dashboard, EnhancedTripRow, ClientManagement, and any future status displays.
 * Icons are lucide-react component references.
 */
import {
  Clock,
  FileText,
  CheckCircle,
  AlertCircle,
  Target,
  Star,
  Heart,
  XCircle,
} from 'lucide-react';

// ── Trip statuses ─────────────────────────────────────────────────────────────

export const TRIP_STATUSES = {
  pending: {
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock,
    description: 'Awaiting questionnaire from client',
  },
  planning: {
    label: 'Planning',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: FileText,
    description: 'Questionnaire submitted, generating itinerary',
  },
  confirmed: {
    label: 'Confirmed',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: CheckCircle,
    description: 'Booked with destination',
  },
  active: {
    label: 'Active',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    icon: Clock,
    description: 'Client is currently on the trip',
  },
  completed: {
    label: 'Completed',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle,
    description: 'Trip finished successfully',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertCircle,
    description: 'Trip was cancelled',
  },
};

/**
 * Get trip status info by status string. Falls back to 'pending'.
 * @param {string} status - Raw status string from API
 * @returns {{ label: string, color: string, icon: Component, description: string }}
 */
export function getTripStatusInfo(status) {
  const key = (status ?? 'pending').toString().toLowerCase();
  return TRIP_STATUSES[key] || TRIP_STATUSES.pending;
}

// ── Client relationship statuses ──────────────────────────────────────────────

export const CLIENT_STATUSES = {
  lead: {
    label: 'Lead',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Target,
    iconColor: 'text-yellow-600',
    description: 'New contact — not yet engaged on a trip',
  },
  prospect: {
    label: 'Prospect',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Star,
    iconColor: 'text-blue-600',
    description: 'Engaged and discussing a trip',
  },
  customer: {
    label: 'Customer',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle,
    iconColor: 'text-green-600',
    description: 'Has completed at least one trip',
  },
  vip: {
    label: 'VIP Client',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: Heart,
    iconColor: 'text-purple-600',
    description: 'High-value repeat client',
  },
  inactive: {
    label: 'Inactive',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: XCircle,
    iconColor: 'text-gray-500',
    description: 'No recent activity',
  },
};

/**
 * Get client status info by relationship_status string.
 * @param {string} status
 * @returns {{ label: string, color: string, icon: Component, iconColor: string, description: string }}
 */
export function getClientStatusInfo(status) {
  const key = (status ?? 'lead').toString().toLowerCase();
  return CLIENT_STATUSES[key] || { label: status || 'Unknown', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: AlertCircle, iconColor: 'text-gray-500', description: '' };
}

// ── Statuses that indicate a trip status change has side effects ───────────────

export const STATUS_SIDE_EFFECTS = {
  completed: 'This will also update the client\'s relationship status to Customer.',
  cancelled: 'The client will be notified that their trip has been cancelled.',
};
